import { Avatar, Badge, Dropdown, Button, Image, Modal, Space, Tag, Tooltip, Typography, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  PlayCircleFilled,
  SyncOutlined,
} from '@ant-design/icons';
import type { NavigateFunction } from 'react-router-dom';
import { ProductState, ProductSaleType, TrackingMode } from '@erpgold/shared';
import type { ProductTenantType } from '@erpgold/shared';
import type { ERPColumnDef } from '@/components/DataTable/ERPTable';
import { ROUTES } from '@/constants/routes';
import { PRODUCT_STATE_LABEL, PRODUCT_STATE_BADGE_STATUS } from './constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ProductListItem extends ProductTenantType {
  variants?: Array<{ _id: string; variant_code: string }>;
}

export interface BuildColumnsParams {
  /** Map of category _id → display name */
  categoryMap: Map<string, string>;
  /** Clone mutation trigger */
  onClone: (id: string) => Promise<void>;
  /** Delete mutation trigger */
  onDelete: (id: string, name: string) => void;
  navigate: NavigateFunction;
}

// ─── Column builder ───────────────────────────────────────────────────────────

export function buildProductColumns(params: BuildColumnsParams): ERPColumnDef<ProductListItem>[] {
  const { categoryMap, onClone, onDelete, navigate } = params;

  return [
    // 1. Ảnh
    {
      key: 'medias',
      title: 'Ảnh',
      width: 80,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const primary = record.medias?.[0];
        const src = primary?.thumbnail || primary?.url;
        if (src) {
          return (
            <div style={{ position: 'relative', width: 48, height: 48 }}>
              <Image
                src={src}
                width={48}
                height={48}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={false}
                fallback="data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Crect width='48' height='48' fill='%23f5f5f5'/%3E%3C/svg%3E"
              />
              {primary?.type === 'video' && (
                <PlayCircleFilled
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    left: 2,
                    color: 'rgba(255,255,255,0.95)',
                    fontSize: 16,
                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          );
        }
        return (
          <Avatar shape="square" size={48} style={{ background: '#a4193d' }}>
            {record.name?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
        );
      },
    },

    // 2. Mã SP (clickable → detail + copyable) — EG-1691
    {
      key: 'sku',
      dataIndex: 'sku',
      title: 'Mã SP',
      width: 200,
      alwaysVisible: true,
      render: (sku: string, record: ProductListItem) => (
        <Space size={4}>
          <Typography.Link
            onClick={() => navigate(ROUTES.products.detail(record._id))}
            style={{ fontWeight: 500 }}
          >
            {sku ?? '—'}
          </Typography.Link>
          {sku && (
            <Typography.Text copyable={{ text: sku, tooltips: ['Sao chép', 'Đã sao chép'] }} />
          )}
        </Space>
      ),
    },

    // 3. Tên + tên ngắn
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Tên sản phẩm',
      width: 300,
      alwaysVisible: true,
      render: (name: string, record: ProductListItem) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          {record.short_name && (
            <div style={{ fontSize: 12, color: '#888' }}>{record.short_name}</div>
          )}
        </div>
      ),
    },

    // 4. Danh mục
    {
      key: 'category_id',
      title: 'Danh mục',
      width: 140,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const ids: string[] = Array.isArray(record.category_id) ? record.category_id : [];
        if (ids.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
        const visible = ids.slice(0, 2);
        const hidden = ids.slice(2);
        return (
          <Space size={4} wrap>
            {visible.map((id) => (
              <Tag key={id}>{categoryMap.get(id) ?? id}</Tag>
            ))}
            {hidden.length > 0 && (
              <Tooltip title={hidden.map((id) => categoryMap.get(id) ?? id).join(', ')}>
                <Tag>+{hidden.length}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },

    // 5. Hình thức bán
    {
      key: 'sale_type',
      title: 'Hình thức bán',
      width: 120,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const types: ProductSaleType[] = Array.isArray(record.sale_type)
          ? record.sale_type
          : record.sale_type
            ? [record.sale_type]
            : [];
        const labelMap: Record<ProductSaleType, string> = {
          [ProductSaleType.ONLINE]: 'Online',
          [ProductSaleType.STORE]: 'Tại cửa hàng',
        };
        if (types.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
        return (
          <Space size={4} wrap>
            {types.map((t) => (
              <Tag key={t}>{labelMap[t] ?? t}</Tag>
            ))}
          </Space>
        );
      },
    },

    // 6. Quản lý tồn kho
    {
      key: 'tracking_mode',
      title: 'Quản lý tồn kho',
      width: 130,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        if (record.tracking_mode === TrackingMode.SERIAL) {
          return <Tag color="red">Mã quy cách</Tag>;
        }
        if (record.tracking_mode === TrackingMode.QUANTITY) {
          return <Tag color="gold">Số lượng</Tag>;
        }
        return <span style={{ color: '#bbb' }}>—</span>;
      },
    },

    // 7. Biến thể
    {
      key: 'variants',
      title: 'Biến thể',
      width: 220,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const variants = record.variants ?? [];
        if (variants.length === 0) return <span style={{ color: '#bbb' }}>—</span>;
        const visible = variants.slice(0, 3);
        const hidden = variants.slice(3);
        return (
          <Space size={4} wrap>
            {visible.map((v) => (
              <Tag key={v._id}>{v.variant_code}</Tag>
            ))}
            {hidden.length > 0 && (
              <Tooltip title={hidden.map((v) => v.variant_code).join(', ')}>
                <Tag>+{hidden.length}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },

    // 8. Trạng thái
    {
      key: 'state',
      dataIndex: 'state',
      title: 'Trạng thái',
      width: 100,
      alwaysVisible: true,
      render: (state: ProductState) => (
        <Badge
          status={PRODUCT_STATE_BADGE_STATUS[state] ?? 'default'}
          text={PRODUCT_STATE_LABEL[state] ?? 'Không xác định'}
        />
      ),
    },

    // 9. Actions (3-dot dropdown)
    {
      key: '__action',
      title: '',
      width: 60,
      fixed: 'right' as const,
      alwaysVisible: true,
      render: (_: unknown, record: ProductListItem) => {
        const items: MenuProps['items'] = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Xem trước',
            onClick: () => navigate(ROUTES.products.detail(record._id)),
          },
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Sửa',
            onClick: () => navigate(ROUTES.products.edit(record._id)),
          },
          {
            key: 'clone',
            icon: <CopyOutlined />,
            label: 'Nhân bản',
            onClick: () => {
              Modal.confirm({
                title: 'Nhân bản sản phẩm',
                content: `Bạn muốn tạo bản sao của sản phẩm "${record.name}"?`,
                okText: 'Nhân bản',
                cancelText: 'Huỷ',
                onOk: () => onClone(record._id),
              });
            },
          },
          {
            key: 'misa',
            icon: <SyncOutlined />,
            label: 'Đồng bộ MISA',
            onClick: () => {
              void message.info('Tính năng đang phát triển');
            },
          },
          { type: 'divider' },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Xoá',
            danger: true,
            onClick: () => onDelete(record._id, record.name),
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} size="small" aria-label="Thao tác" />
          </Dropdown>
        );
      },
    },
  ];
}

// ─── Backward-compat static export (used by index.tsx until migration) ────────

/** @deprecated Use buildProductColumns() instead. */
export const productColumns: ERPColumnDef<ProductTenantType>[] = buildProductColumns({
  categoryMap: new Map(),
  onClone: () => Promise.resolve(),
  onDelete: () => undefined,
  navigate: () => undefined,
});

export const PRODUCT_TABLE_KEY = 'products-list-v2';
